import { Component } from '@theme/component';

/**
 * Frelan: the Finishes mega menu (blocks/mega-menu-finish-wall.liquid) — the preview in the
 * "popular" and "wall" layouts. The "fold" layout has no preview; there this does nothing and
 * every tap follows its link.
 *
 * Each finish is a real link to its finish, carrying what the preview needs as data
 * attributes (name, family, product count, photo, swatch fill, "Shop …" label). This
 * component only swaps the preview:
 * - mouse hover or keyboard focus previews a finish; a click follows the link;
 * - touch/pen: the first tap on a swatch previews it, a second tap on the same swatch opens it
 *   (the preview's button opens it too).
 * The preview keeps the last finish shown; it does not reset when the pointer leaves.
 *
 * The mobile drawer clones the menu markup (assets/mobile-mega-menu.js strips ids), so
 * everything is found through refs and delegated listeners on this element.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [preview] - absent in the fold layout
 * @property {HTMLAnchorElement} previewMedia
 * @property {HTMLImageElement} previewImg
 * @property {HTMLElement} previewChip
 * @property {HTMLElement} [previewFan] - resting preview (lead swatch of each family)
 * @property {HTMLElement} previewFamily
 * @property {HTMLElement} previewName
 * @property {HTMLElement} previewMeta
 * @property {HTMLAnchorElement} previewCta
 * @property {HTMLElement} previewCtaLong - "Shop Satin Brass" (wide layout)
 * @property {HTMLElement} previewCtaShort - "Shop" (narrow preview bar)
 *
 * @extends {Component<Refs>}
 */
class FinishWall extends Component {
  /** @type {HTMLAnchorElement | null} */
  #selected = null;

  /** Pointer type of the last press, so a click knows whether it was a tap. */
  #pointerType = '';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLAnchorElement | null}
   */
  #swatchFor(target) {
    if (!(target instanceof Element)) return null;
    const swatch = target.closest('.finish-wall__swatch');
    return swatch instanceof HTMLAnchorElement && this.contains(swatch) ? swatch : null;
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const swatch = this.#swatchFor(event.target);
    if (swatch) this.select(swatch);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pointerType = event.pointerType;
  };

  /**
   * Keyboard focus previews. A tap also focuses the link (before its click), so only
   * :focus-visible counts here — otherwise the first tap would already open the finish.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const swatch = this.#swatchFor(event.target);
    if (swatch?.matches(':focus-visible')) this.select(swatch);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const swatch = this.#swatchFor(event.target);
    const isTap = this.#pointerType === 'touch' || this.#pointerType === 'pen';
    this.#pointerType = '';
    // Layouts without a preview (the mobile fold-out) just follow the link.
    if (!swatch || !isTap || !this.refs.preview || swatch === this.#selected) return;

    event.preventDefault();
    this.select(swatch);
  };

  /**
   * Shows a finish in the preview.
   * @param {HTMLAnchorElement} swatch
   */
  select(swatch) {
    if (swatch === this.#selected || !this.refs.preview) return;

    this.#selected?.removeAttribute('data-selected');
    swatch.setAttribute('data-selected', '');
    this.#selected = swatch;

    const {
      previewMedia,
      previewImg,
      previewChip,
      previewFan,
      previewFamily,
      previewName,
      previewMeta,
      previewCta,
      previewCtaLong,
      previewCtaShort,
    } = this.refs;
    const { name = '', family = '', meta = '', cta = '', chip = '', photo = '', photoSrcset = '' } = swatch.dataset;

    previewFan?.setAttribute('hidden', '');

    if (photo) {
      previewImg.srcset = photoSrcset;
      previewImg.src = photo;
      previewImg.hidden = false;
      previewChip.hidden = true;
    } else {
      previewImg.hidden = true;
      previewChip.style.setProperty('--finish-wall-chip', chip);
      previewChip.hidden = false;
    }

    previewFamily.textContent = family;
    previewName.textContent = name;
    previewMeta.textContent = meta;
    previewCtaLong.textContent = cta;
    previewCtaShort.textContent = previewCta.dataset.shortLabel || cta;
    previewMedia.href = swatch.href;
    previewCta.href = swatch.href;
  }
}

if (!customElements.get('finish-wall')) {
  customElements.define('finish-wall', FinishWall);
}
